import I18n from 'I18n';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import Products from 'merchandise-lib/constants/Product';
import { useMemo } from 'react';
import { useGetAddOnsQuery, useGetOwnedQuantitiesQuery } from '../../../../__generated__/chirp/com/hubspot/buying/rpc/ProductRpc.hooks';
import { useEvaluateInAppQuery } from '../../../../__generated__/chirp/com/hubspot/purchase/motions/rpc/PurchaseMotionsEvaluationService.hooks';
import { useProductInventory } from '../inventory/useProductInventory';
import { useGrowthPopPurchaseOptions } from '../pricing/useGrowthPurchaseOptions';
import { useProductDetailsForArray } from '../product/useProductDetails';
const getAddOnTitleKey = productApiName => {
  return `pricing-pages-ui.productNamesV2.${productApiName}.title`;
};
const getTranslatedAddOns = eligibleAddOns => {
  return eligibleAddOns.filter(addOn => isValidI18nKey(getAddOnTitleKey(addOn))).map(productApiName => ({
    productApiName,
    titleText: I18n.text(`pricing-pages-ui.productNamesV2.${productApiName}.title`),
    descriptionText: I18n.text(`pricing-pages-ui.productNamesV2.${productApiName}.paragraph`)
  }));
};
export function useAddOnsData({
  addOnApiNames
}) {
  var _purchaseMotionsQuery;
  const skipDetailedQueries = addOnApiNames.length === 0;
  const {
    productDetails: allProductDetails,
    loading: productDetailsLoading,
    error: productDetailsError
  } = useProductDetailsForArray({
    productApiNames: addOnApiNames
  });
  const {
    products: inventoryProducts,
    loading: inventoryLoading,
    error: inventoryError
  } = useProductInventory();
  const purchaseMotionsQuery = useEvaluateInAppQuery({
    variables: {
      productApiNames: addOnApiNames,
      locations: ['MERCH_CARD']
    },
    skip: skipDetailedQueries
  });
  const productsToPurchase = useMemo(() => {
    return addOnApiNames.map(productApiName => ({
      productApiName,
      quantity: 1
    }));
  }, [addOnApiNames]);
  const {
    products: allPricingData,
    loading: pricingLoading,
    currencyCode,
    error: pricingError
  } = useGrowthPopPurchaseOptions({
    productsToPurchase
  });
  const hasHubSpotCreditsInList = useMemo(() => {
    return addOnApiNames.some(apiName => apiName === Products.HUBSPOT_CREDITS_COMMITTED || apiName === Products.HUBSPOT_CREDITS_COMMITTED_OVERAGE || apiName === Products.HUBSPOT_CREDITS_OVERAGE);
  }, [addOnApiNames]);
  const hubspotCreditsQuery = useGetOwnedQuantitiesQuery({
    variables: {
      quantityNames: ['HUBSPOT_CREDITS']
    },
    skip: !hasHubSpotCreditsInList
  });
  const isLoading = productDetailsLoading || inventoryLoading || purchaseMotionsQuery.loading || pricingLoading;
  const hasError = !!(productDetailsError || inventoryError || purchaseMotionsQuery.error || pricingError || hubspotCreditsQuery.error);
  return {
    allProductDetails,
    inventoryProducts,
    purchaseMotionsMapping: (_purchaseMotionsQuery = purchaseMotionsQuery.data) === null || _purchaseMotionsQuery === void 0 ? void 0 : _purchaseMotionsQuery.purchaseMotionsMapping,
    allPricingData,
    currencyCode,
    hubspotCreditsData: hubspotCreditsQuery.data,
    isLoading: skipDetailedQueries ? false : isLoading,
    hasError
  };
}
export function useEligibleAddOns() {
  var _addOnsQuery$data2;
  const addOnsQuery = useGetAddOnsQuery();
  const validAddOns = useMemo(() => {
    var _addOnsQuery$data;
    if (!((_addOnsQuery$data = addOnsQuery.data) !== null && _addOnsQuery$data !== void 0 && _addOnsQuery$data.eligibleAddOns)) {
      return [];
    }
    return getTranslatedAddOns(addOnsQuery.data.eligibleAddOns);
  }, [(_addOnsQuery$data2 = addOnsQuery.data) === null || _addOnsQuery$data2 === void 0 ? void 0 : _addOnsQuery$data2.eligibleAddOns]);
  const allAddOnApiNames = useMemo(() => {
    return validAddOns.map(addOn => addOn.productApiName);
  }, [validAddOns]);
  return {
    validAddOns,
    allAddOnApiNames,
    isLoading: addOnsQuery.loading,
    hasError: !!addOnsQuery.error
  };
}